import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Activity,
  ArrowLeft,
  BadgeDollarSign,
  Ban,
  BookCheck,
  CheckCircle2,
  Coins,
  Database,
  KeyRound,
  LogIn,
  RefreshCw,
  RotateCcw,
  Settings2,
  ShieldCheck,
  UserPlus,
  Users,
  WalletCards,
  XCircle,
} from 'lucide-react'
import { api, ApiError } from './client/api'
import { authClient } from './client/auth'
import type { AdminDashboard, PlayerSession } from './shared/contracts'

type AdminTab = 'users' | 'economy' | 'pools' | 'community' | 'ledger'

const tabs: { id: AdminTab; label: string; icon: typeof Users }[] = [
  { id: 'users', label: 'Accounts', icon: Users },
  { id: 'economy', label: 'Economy', icon: Activity },
  { id: 'pools', label: 'Reward pools', icon: Coins },
  { id: 'community', label: 'Community', icon: BookCheck },
  { id: 'ledger', label: 'Audit ledger', icon: Database },
]

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
const quantity = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 })
const shortDate = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })

function formValue(data: FormData, key: string): string {
  return String(data.get(key) ?? '').trim()
}

function actionKey(prefix: string): string {
  return `${prefix}:${crypto.randomUUID()}`
}

export function AdminPage() {
  const [session, setSession] = useState<PlayerSession | null>()
  const [dashboard, setDashboard] = useState<AdminDashboard>()
  const [tab, setTab] = useState<AdminTab>('users')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string>()
  const [error, setError] = useState<string>()
  const [resetUserId, setResetUserId] = useState<string>()

  const load = useCallback(async () => {
    setError(undefined)
    try {
      const current = await api.me()
      setSession(current)
      if (current?.user.role === 'admin') setDashboard(await api.adminDashboard())
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setSession(null)
        return
      }
      setError(caught instanceof Error ? caught.message : 'Admin console is unavailable.')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true)
    setError(undefined)
    setMessage(undefined)
    try {
      await action()
      setMessage(success)
      await load()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The operation failed.')
    } finally {
      setBusy(false)
    }
  }

  async function signIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    setBusy(true)
    setError(undefined)
    const result = await authClient.signIn.email({ email: formValue(data, 'email'), password: formValue(data, 'password') })
    setBusy(false)
    if (result.error) {
      setError(result.error.message ?? 'Sign in failed.')
      return
    }
    await load()
  }

  if (session === undefined && !error) {
    return <main className="admin-gate"><div className="admin-loader"><RefreshCw aria-hidden="true" /> Authenticating console…</div></main>
  }

  if (!session) {
    return (
      <main className="admin-gate">
        <section className="admin-login-card">
          <div className="admin-brand-mark"><ShieldCheck aria-hidden="true" /></div>
          <p className="eyebrow">PUMPSWIM OPERATIONS</p>
          <h1>Admin current control</h1>
          <p>Secure access for virtual economy operators. Every balance and pool mutation is written to the audit ledger.</p>
          <form onSubmit={signIn} className="admin-form-stack">
            <label>ADMIN EMAIL<input name="email" type="email" autoComplete="username" required /></label>
            <label>PASSWORD<input name="password" type="password" autoComplete="current-password" required /></label>
            <button className="primary-button" disabled={busy}><LogIn aria-hidden="true" /> SIGN IN</button>
          </form>
          {error && <p className="admin-alert error">{error}</p>}
          <a className="admin-back-link" href="/"><ArrowLeft aria-hidden="true" /> Return to the game</a>
        </section>
      </main>
    )
  }

  if (session.user.role !== 'admin') {
    return (
      <main className="admin-gate">
        <section className="admin-login-card">
          <XCircle className="admin-denied" aria-hidden="true" />
          <p className="eyebrow">ACCESS DENIED</p>
          <h1>Operator role required</h1>
          <p>{session.user.email} is signed in, but this account cannot operate the virtual economy.</p>
          <a className="primary-button" href="/"><ArrowLeft aria-hidden="true" /> RETURN TO GAME</a>
        </section>
      </main>
    )
  }

  const activeTab = tabs.find((item) => item.id === tab)!
  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <a className="admin-logo" href="/"><span><ShieldCheck aria-hidden="true" /></span><strong>PUMPSWIM<small>ECONOMY OPS</small></strong></a>
        <nav aria-label="Admin console sections">
          {tabs.map((item) => <button key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => setTab(item.id)}><item.icon aria-hidden="true" />{item.label}</button>)}
        </nav>
        <div className="admin-operator"><span className="status-dot" /><div><small>AUTHENTICATED AS</small><strong>{session.user.email}</strong></div></div>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <div><p className="eyebrow">VIRTUAL ASSETS · AUDITED CONTROL</p><h1><activeTab.icon aria-hidden="true" /> {activeTab.label}</h1></div>
          <div className="admin-header-actions"><span className="admin-live"><span /> SYSTEM LIVE</span><button className="icon-button" aria-label="Refresh dashboard" onClick={() => void load()}><RefreshCw aria-hidden="true" /></button><a className="admin-exit" href="/"><ArrowLeft aria-hidden="true" /> Game</a></div>
        </header>

        {(message || error) && <div className={`admin-alert ${error ? 'error' : 'success'}`}>{error ? <XCircle aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />}{error ?? message}</div>}
        {!dashboard ? <div className="admin-loader"><RefreshCw aria-hidden="true" /> Loading audited state…</div> : (
          <>
            {tab === 'users' && <AccountsPanel dashboard={dashboard} busy={busy} resetUserId={resetUserId} setResetUserId={setResetUserId} run={run} />}
            {tab === 'economy' && <EconomyPanel dashboard={dashboard} busy={busy} run={run} />}
            {tab === 'pools' && <PoolsPanel dashboard={dashboard} busy={busy} run={run} />}
            {tab === 'community' && <CommunityPanel dashboard={dashboard} busy={busy} run={run} />}
            {tab === 'ledger' && <LedgerPanel dashboard={dashboard} />}
          </>
        )}
      </main>
    </div>
  )
}

function AccountsPanel({ dashboard, busy, resetUserId, setResetUserId, run }: {
  dashboard: AdminDashboard
  busy: boolean
  resetUserId?: string
  setResetUserId: (id?: string) => void
  run: (action: () => Promise<unknown>, success: string) => Promise<void>
}) {
  const totals = useMemo(() => ({
    active: dashboard.users.filter((user) => !user.banned).length,
    forced: dashboard.users.filter((user) => user.mustChangePassword).length,
    swim: dashboard.users.reduce((sum, user) => sum + Number(user.swimAvailable), 0),
  }), [dashboard.users])

  function createUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    void run(() => api.adminCreateUser({
      name: formValue(data, 'name'),
      email: formValue(data, 'email'),
      temporaryPassword: formValue(data, 'temporaryPassword'),
      role: formValue(data, 'role') as 'user' | 'admin',
    }), 'Account created with a forced password change.').then(() => form.reset())
  }

  function adjustWallet(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    void run(() => api.adminAdjustWallet({
      userId: formValue(data, 'userId'),
      asset: formValue(data, 'asset') as 'USD_VIRTUAL' | 'SWIM',
      amount: formValue(data, 'amount'),
      reason: formValue(data, 'reason'),
      idempotencyKey: actionKey('admin-wallet'),
    }), 'Wallet adjustment committed to the ledger.').then(() => form.reset())
  }

  return (
    <div className="admin-content-stack">
      <section className="admin-stat-grid">
        <article><Users aria-hidden="true" /><small>TOTAL ACCOUNTS</small><strong>{dashboard.users.length}</strong><span>{totals.active} active</span></article>
        <article><KeyRound aria-hidden="true" /><small>FORCED RESET</small><strong>{totals.forced}</strong><span>cannot play until changed</span></article>
        <article><Coins aria-hidden="true" /><small>PLAYER SWIM</small><strong>{quantity.format(totals.swim)}</strong><span>virtual circulation</span></article>
      </section>

      <div className="admin-two-column">
        <section className="admin-panel"><header><div><p className="eyebrow">CONTROLLED ONBOARDING</p><h2><UserPlus aria-hidden="true" /> Create account</h2></div></header><form className="admin-form-grid" onSubmit={createUser}>
          <label>DISPLAY NAME<input name="name" required minLength={2} /></label>
          <label>EMAIL<input name="email" type="email" required /></label>
          <label>TEMPORARY PASSWORD<input name="temporaryPassword" type="password" required minLength={12} autoComplete="new-password" /></label>
          <label>ROLE<select name="role" defaultValue="user"><option value="user">Player</option><option value="admin">Admin</option></select></label>
          <button className="primary-button" disabled={busy}><UserPlus aria-hidden="true" /> CREATE + GRANT $10,000</button>
        </form></section>

        <section className="admin-panel"><header><div><p className="eyebrow">IMMUTABLE AUDIT ENTRY</p><h2><WalletCards aria-hidden="true" /> Adjust wallet</h2></div></header><form className="admin-form-grid" onSubmit={adjustWallet}>
          <label>ACCOUNT<select name="userId" required>{dashboard.users.map((user) => <option key={user.id} value={user.id}>{user.email}</option>)}</select></label>
          <label>ASSET<select name="asset"><option value="USD_VIRTUAL">Virtual USD</option><option value="SWIM">SWIM</option></select></label>
          <label>AMOUNT<input name="amount" inputMode="decimal" placeholder="+100 or -25" required pattern="-?\d+(\.\d{1,6})?" /></label>
          <label>REASON<input name="reason" required minLength={3} placeholder="Support case / campaign" /></label>
          <button className="secondary-button" disabled={busy}><BadgeDollarSign aria-hidden="true" /> COMMIT ADJUSTMENT</button>
        </form></section>
      </div>

      <section className="admin-panel"><header><div><p className="eyebrow">IDENTITY + BALANCES</p><h2>Player accounts</h2></div><span>{dashboard.users.length} records</span></header><div className="admin-table-wrap"><table><thead><tr><th>Account</th><th>Status</th><th>Virtual USD</th><th>SWIM</th><th>Created</th><th>Controls</th></tr></thead><tbody>{dashboard.users.map((user) => <tr key={user.id}>
        <td><strong>{user.name}</strong><small>{user.email}</small></td>
        <td><span className={`admin-badge ${user.banned ? 'danger' : user.mustChangePassword ? 'warning' : 'ok'}`}>{user.banned ? 'BANNED' : user.mustChangePassword ? 'RESET REQUIRED' : user.role.toUpperCase()}</span></td>
        <td><strong>{usd.format(Number(user.usdAvailable))}</strong><small>{usd.format(Number(user.usdLocked))} locked</small></td>
        <td>{quantity.format(Number(user.swimAvailable))}</td>
        <td>{shortDate.format(user.createdAt)}</td>
        <td><div className="admin-row-actions"><button onClick={() => void run(() => api.adminBanUser(user.id, !user.banned, user.banned ? undefined : 'Admin console action'), user.banned ? 'Account unbanned.' : 'Account banned and sessions revoked.')} disabled={busy || user.role === 'admin'}>{user.banned ? <CheckCircle2 /> : <Ban />}{user.banned ? 'Unban' : 'Ban'}</button><button onClick={() => setResetUserId(resetUserId === user.id ? undefined : user.id)} disabled={user.role === 'admin'}><KeyRound /> Reset</button></div>{resetUserId === user.id && <form className="admin-inline-form" onSubmit={(event) => { event.preventDefault(); const password = formValue(new FormData(event.currentTarget), 'temporaryPassword'); void run(() => api.adminResetPassword(user.id, password), 'Temporary password set; all sessions revoked.').then(() => setResetUserId(undefined)) }}><input name="temporaryPassword" type="password" minLength={12} required placeholder="New temporary password" /><button disabled={busy}>SET</button></form>}</td>
      </tr>)}</tbody></table></div></section>
    </div>
  )
}

function EconomyPanel({ dashboard, busy, run }: { dashboard: AdminDashboard; busy: boolean; run: (action: () => Promise<unknown>, success: string) => Promise<void> }) {
  return <div className="admin-content-stack">
    <section className="admin-panel"><header><div><p className="eyebrow">DB OVERRIDE &gt; ENV &gt; DEFAULT</p><h2><Settings2 aria-hidden="true" /> Economy settings</h2></div><span>New bets and Raids only</span></header><div className="admin-setting-grid">{dashboard.settings.map((setting) => <form key={setting.key} onSubmit={(event) => { event.preventDefault(); const value = Number(formValue(new FormData(event.currentTarget), 'value')); void run(() => api.adminSetSetting(setting.key, value), `${setting.envKey} overridden.`) }}><div><strong>{setting.key}</strong><small>{setting.envKey}</small></div><span className={`admin-source ${setting.source}`}>{setting.source}</span><input name="value" type="number" min="0" step="any" defaultValue={setting.value} required /><button disabled={busy}>SAVE</button>{setting.source === 'database' && <button type="button" className="reset" onClick={() => void run(() => api.adminClearSetting(setting.key), `${setting.envKey} restored.`)}><RotateCcw aria-hidden="true" /></button>}</form>)}</div></section>
    <section className="admin-panel"><header><div><p className="eyebrow">UTC DAILY ROLLUP</p><h2><Activity aria-hidden="true" /> NGR and Raid emission</h2></div></header><div className="admin-table-wrap"><table><thead><tr><th>Day</th><th>Volume</th><th>Loss stakes</th><th>Win profit</th><th>Cash pool</th><th>NGR</th><th>EMA7</th><th>Raid top-up</th></tr></thead><tbody>{dashboard.metrics.map((metric) => <tr key={metric.dayKey}><td><strong>{metric.dayKey}</strong></td><td>{usd.format(Number(metric.wagerVolume))}</td><td>{usd.format(Number(metric.losingStakes))}</td><td>{usd.format(Number(metric.winningProfit))}</td><td>{usd.format(Number(metric.cashContribution))}</td><td><strong>{usd.format(Number(metric.ngr))}</strong></td><td>{usd.format(Number(metric.emaNgr))}</td><td><span className="admin-badge ok">{quantity.format(Number(metric.raidTopUp))} SWIM</span></td></tr>)}</tbody></table></div></section>
  </div>
}

function PoolsPanel({ dashboard, busy, run }: { dashboard: AdminDashboard; busy: boolean; run: (action: () => Promise<unknown>, success: string) => Promise<void> }) {
  function adjust(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    void run(() => api.adminAdjustPool({ poolId: formValue(data, 'poolId'), amount: formValue(data, 'amount'), reason: formValue(data, 'reason'), idempotencyKey: actionKey('admin-pool') }), 'Pool adjustment committed to the ledger.').then(() => form.reset())
  }
  return <div className="admin-content-stack"><section className="admin-pool-grid">{dashboard.pools.map((pool) => <article key={pool.id} className={pool.type.includes('cash') ? 'cash' : pool.type.includes('raid') ? 'raid' : 'swim'}><Coins aria-hidden="true" /><small>{pool.type.replaceAll('_', ' ').toUpperCase()}</small><strong>{quantity.format(Number(pool.available))} {pool.type.includes('cash') ? 'USD' : 'SWIM'}</strong><span>{quantity.format(Number(pool.locked))} reserved · {pool.periodKey}</span><code>{pool.id}</code></article>)}</section><section className="admin-panel"><header><div><p className="eyebrow">SEED OR CORRECT WITH REASON</p><h2><BadgeDollarSign aria-hidden="true" /> Adjust reward pool</h2></div></header><form className="admin-form-grid admin-pool-form" onSubmit={adjust}><label>POOL<select name="poolId" required>{dashboard.pools.map((pool) => <option key={pool.id} value={pool.id}>{pool.type} · {pool.periodKey}</option>)}</select></label><label>AMOUNT<input name="amount" required inputMode="decimal" placeholder="+1000 or -50" pattern="-?\d+(\.\d{1,6})?" /></label><label>REASON<input name="reason" required minLength={3} placeholder="Campaign seed / reconciliation" /></label><button className="primary-button" disabled={busy}>COMMIT TO POOL LEDGER</button></form></section></div>
}

function CommunityPanel({ dashboard, busy, run }: { dashboard: AdminDashboard; busy: boolean; run: (action: () => Promise<unknown>, success: string) => Promise<void> }) {
  function createQuest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    const startsAt = new Date(formValue(data, 'startsAt')).getTime()
    const endsAt = new Date(formValue(data, 'endsAt')).getTime()
    void run(() => api.adminCreateQuest({ title: formValue(data, 'title'), description: formValue(data, 'description'), instructions: formValue(data, 'instructions'), rewardSwim: formValue(data, 'rewardSwim'), startsAt, endsAt }), 'Social quest published.').then(() => form.reset())
  }
  return <div className="admin-content-stack"><div className="admin-two-column"><section className="admin-panel"><header><div><p className="eyebrow">MANUAL PROOF REVIEW</p><h2><BookCheck aria-hidden="true" /> Publish quest</h2></div></header><form className="admin-form-grid" onSubmit={createQuest}><label>TITLE<input name="title" required minLength={3} /></label><label>SWIM REWARD<input name="rewardSwim" required inputMode="decimal" /></label><label className="wide">DESCRIPTION<textarea name="description" required minLength={3} /></label><label className="wide">INSTRUCTIONS<textarea name="instructions" required minLength={3} /></label><label>START UTC<input name="startsAt" type="datetime-local" required /></label><label>END UTC<input name="endsAt" type="datetime-local" required /></label><button className="primary-button" disabled={busy}>PUBLISH QUEST</button></form></section><section className="admin-panel"><header><div><p className="eyebrow">REFERRAL QUALIFICATION</p><h2>Referral progress</h2></div><span>{dashboard.referrals.length} links</span></header><div className="admin-compact-list">{dashboard.referrals.length === 0 ? <p className="admin-empty">No captured referrals yet.</p> : dashboard.referrals.map((referral) => <article key={referral.id}><div><strong>{referral.referrerEmail}</strong><small>referred {referral.referredEmail}</small></div><span>{usd.format(Number(referral.qualifyingVolume))}</span><em className={`admin-badge ${referral.rewarded ? 'ok' : 'warning'}`}>{referral.rewarded ? 'REWARDED' : 'TRACKING'}</em></article>)}</div></section></div><section className="admin-panel"><header><div><p className="eyebrow">PROOF URL · ONE-TIME LEDGER REWARD</p><h2>Quest claims</h2></div><span>{dashboard.questClaims.filter((claim) => claim.status === 'pending').length} pending</span></header><div className="admin-claim-grid">{dashboard.questClaims.length === 0 ? <p className="admin-empty">No quest claims awaiting review.</p> : dashboard.questClaims.map((claim) => <article key={claim.id}><div><span className={`admin-badge ${claim.status === 'approved' ? 'ok' : claim.status === 'rejected' ? 'danger' : 'warning'}`}>{claim.status}</span><small>{shortDate.format(claim.createdAt)}</small></div><a href={claim.proofUrl} target="_blank" rel="noreferrer">{claim.proofUrl}</a><p>{claim.note || 'No player note.'}</p>{claim.status === 'pending' && <form onSubmit={(event) => { event.preventDefault(); const form = event.currentTarget; const note = formValue(new FormData(form), 'reviewerNote'); const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement; const decision = submitter.value as 'approved' | 'rejected'; void run(() => api.adminReviewQuestClaim(claim.id, decision, note), `Claim ${decision}.`) }}><input name="reviewerNote" required minLength={3} placeholder="Reviewer note" /><button name="decision" value="approved" disabled={busy}><CheckCircle2 /> Approve</button><button name="decision" value="rejected" className="danger" disabled={busy}><XCircle /> Reject</button></form>}</article>)}</div></section></div>
}

function LedgerPanel({ dashboard }: { dashboard: AdminDashboard }) {
  return <section className="admin-panel"><header><div><p className="eyebrow">APPEND-ONLY ECONOMY TRACE</p><h2><Database aria-hidden="true" /> Latest ledger entries</h2></div><span>{dashboard.ledger.length} entries loaded</span></header><div className="admin-table-wrap"><table><thead><tr><th>Time</th><th>Scope</th><th>Owner</th><th>Type</th><th>Asset</th><th>Available Δ</th><th>Locked Δ</th><th>Reason</th></tr></thead><tbody>{dashboard.ledger.map((entry) => <tr key={`${entry.scope}:${entry.id}`}><td>{shortDate.format(entry.createdAt)}</td><td><span className={`admin-badge ${entry.scope === 'pool' ? 'warning' : 'ok'}`}>{entry.scope}</span></td><td><strong>{entry.owner}</strong></td><td>{entry.type.replaceAll('_', ' ')}</td><td>{entry.asset}</td><td className={Number(entry.availableDelta) < 0 ? 'negative' : 'positive'}>{Number(entry.availableDelta) > 0 ? '+' : ''}{quantity.format(Number(entry.availableDelta))}</td><td>{quantity.format(Number(entry.lockedDelta))}</td><td>{entry.reason ?? '—'}</td></tr>)}</tbody></table></div></section>
}
